import React from 'react';

import Axios from 'axios';

import API from 'utils/api';

import { useInfiniteSearch } from 'utils/hooks/infiniteSearch';

import { APIRequest, SearchRequest } from 'utils/api/types';
import { UserSearchResponse } from 'utils/api/modules/user/user.types';
import { MemberGroupSearchResult } from 'utils/api/modules/memberGroups/memberGroups.types';

import { PickerCategory, PickerItemKind, PickerMode, PickerOption, PickerValue } from './types';

const PAGE_SIZE = 20;

interface PickerSearchParams {
  enableGroups: boolean;
  licenses?: string;
  role?: string;
  excludeMemberGroupUuid?: string;
  predefinedOptions: PickerValue[];
}

interface PickerSearch {
  groupsAvailable: boolean;
  mode: PickerMode;
  query: string;
  rows: PickerOption[];
  loading: boolean;
  pending: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
  openCategory: (category: PickerCategory) => void;
  openUsers: () => void;
  goToRoot: () => void;
  closeCategory: () => void;
  search: (phrase: string) => void;
}

export const usePickerSearch = ({
  enableGroups,
  licenses,
  role,
  excludeMemberGroupUuid,
  predefinedOptions,
}: PickerSearchParams): PickerSearch => {
  const [mode, setMode] = React.useState<PickerMode>(enableGroups ? 'root' : PickerCategory.Users);
  const [query, setQuery] = React.useState('');
  const [loadUsers, setLoadUsers] = React.useState(false);
  const [loadGroups, setLoadGroups] = React.useState(false);

  const [orgHasGroups, setOrgHasGroups] = React.useState<boolean | null>(null);
  const popupOpen = React.useRef(false);

  const isGroups = mode === PickerCategory.Groups;
  // Unknown or failed counts as having groups, so a slow check never hides a real choice.
  const groupsAvailable = enableGroups && orgHasGroups !== false;

  React.useEffect(() => {
    if (!enableGroups) return undefined;
    const cancelTokenSource = Axios.CancelToken.source();
    (async () => {
      try {
        const { data } = await API.memberGroups.searchMemberGroups({ limit: 1 }, cancelTokenSource)();
        setOrgHasGroups(data.length > 0);
      } catch (error) {
        if (Axios.isCancel(error)) return;
        setOrgHasGroups(null);
      }
    })();
    return () => cancelTokenSource.cancel();
  }, [enableGroups]);

  const searchUsers = React.useCallback(
    (params?: SearchRequest): APIRequest<UserSearchResponse[]> =>
      API.user.searchUsers({ ...params, licenses, role, exclude_member_group: excludeMemberGroupUuid }),
    [licenses, role, excludeMemberGroupUuid]
  );

  const searchGroups = React.useCallback(
    (params?: SearchRequest): APIRequest<MemberGroupSearchResult[]> =>
      API.memberGroups.searchMemberGroups({ query: params?.query, limit: params?.limit, offset: params?.offset }),
    []
  );

  const {
    results: userResults,
    loading: loadingUsers,
    loadingMore: loadingMoreUsers,
    hasMore: hasMoreUsers,
    loadMore: loadMoreUsers,
    appliedQuery: appliedUserQuery,
    searchQuery: queryUsers,
  } = useInfiniteSearch<UserSearchResponse>(searchUsers, loadUsers, { pageSize: PAGE_SIZE });

  const {
    results: groupResults,
    loading: loadingGroups,
    loadingMore: loadingMoreGroups,
    hasMore: hasMoreGroups,
    loadMore: loadMoreGroups,
    appliedQuery: appliedGroupQuery,
    searchQuery: queryGroups,
  } = useInfiniteSearch<MemberGroupSearchResult>(searchGroups, loadGroups, { pageSize: PAGE_SIZE });

  const predefinedUuids = new Set(predefinedOptions.map((option) => option.uuid));
  // Filtered by the query the results came back for, not the typed one, so both halves of the list
  // always describe the same search.
  const userPhrase = appliedUserQuery.trim().toLowerCase();
  const userRows: PickerOption[] = [
    ...predefinedOptions.filter((option) => option.name.toLowerCase().includes(userPhrase)),
    ...userResults
      .filter((user) => !predefinedUuids.has(user.uuid))
      .map((user) => ({
        kind: PickerItemKind.User as const,
        uuid: user.uuid,
        name: user.name,
        email: user.email,
      })),
  ];

  const groupRows: PickerOption[] = groupResults.map((group) => ({
    kind: PickerItemKind.Group,
    uuid: group.uuid,
    name: group.name,
    memberCount: group.memberCount,
  }));

  const openCategory = (category: PickerCategory): void => {
    setMode(category);
    setQuery('');
    if (category === PickerCategory.Groups) {
      setLoadGroups(true);
      queryGroups('');
    } else {
      setLoadUsers(true);
      queryUsers('');
    }
  };

  // MUI reopens the popup on the keystroke that starts a search, so opening must leave the query
  // alone — resetting it there cancels the debounced search for what was just typed.
  const openUsers = (): void => {
    popupOpen.current = true;
    setMode(PickerCategory.Users);
    setLoadUsers(true);
  };

  const goToRoot = (): void => {
    popupOpen.current = true;
    setMode('root');
    setQuery('');
  };

  // Unlike goToRoot, leaves the query alone — the closed field keeps showing what was typed.
  const closeCategory = (): void => {
    popupOpen.current = false;
    setMode('root');
  };

  React.useEffect(() => {
    if (groupsAvailable || mode === PickerCategory.Users) return;
    setMode(PickerCategory.Users);
    if (!popupOpen.current) return;
    setQuery('');
    setLoadUsers(true);
  }, [groupsAvailable, mode]);

  const search = (phrase: string): void => {
    setQuery(phrase);
    if (isGroups) {
      queryGroups(phrase);
    } else if (mode === PickerCategory.Users) {
      queryUsers(phrase);
    }
  };

  return {
    groupsAvailable,
    mode,
    query,
    rows: isGroups ? groupRows : userRows,
    loading: isGroups ? loadingGroups : loadingUsers,
    pending: query.trim() !== (isGroups ? appliedGroupQuery : appliedUserQuery).trim(),
    hasMore: isGroups ? hasMoreGroups : hasMoreUsers,
    loadingMore: isGroups ? loadingMoreGroups : loadingMoreUsers,
    loadMore: isGroups ? loadMoreGroups : loadMoreUsers,
    openCategory,
    openUsers,
    goToRoot,
    closeCategory,
    search,
  };
};
